<template>
    <li class="w-full text-lg text-theme-text">
        <button
            v-if="hasNavigationTarget"
            type="button"
            :disabled="isPreviewMode"
            :class="{
                'font-bold': hasChildren,
                // The accent wash is a 10-20% tint, 1.16-1.44:1 against the panel across
                // the seven themes, so the border is what actually carries the cue: a
                // shape rather than a hue, clearing 3:1 everywhere without darkening the
                // row's own text. Both border colours are gated so neither can rely on
                // the order Tailwind happens to emit them in.
                'border-theme-accent': isCurrentItem || isCurrentAncestor,
                'bg-theme-accent-bg': isCurrentItem,
                'border-transparent': !isCurrentItem && !isCurrentAncestor,
                // Hover shares the background property and wins on specificity, so the
                // current entry would lose its highlight exactly while pointed at.
                'hover:bg-theme-icon-bg': !isCurrentItem,
            }"
            :aria-current="isCurrentItem ? 'location' : null"
            :data-autofocus="isAutofocusItem ? '' : null"
            class="grid w-full cursor-pointer grid-cols-12 gap-4 rounded border-l-2 px-3 py-0.5 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-theme-accent disabled:cursor-default"
            @click.stop="goToItem(item)"
        >
            <span class="col-span-10" dusk="itemContentList">
                {{ item.label }}
            </span>
            <span v-if="item.page" class="col-span-2 text-right" dusk="itemPage">
                {{ item.page }}
            </span>
        </button>
        <div
            v-else
            :class="{
                'border-theme-accent': isCurrentAncestor,
                'border-transparent': !isCurrentAncestor,
            }"
            class="grid w-full grid-cols-12 gap-4 rounded border-l-2 px-3 py-0.5 text-left font-bold"
        >
            <span class="col-span-10" dusk="itemContentList">
                {{ item.label }}
            </span>
        </div>
        <ul v-if="hasChildren" class="pl-1">
            <content-list
                v-for="(childItem, index) in item.items"
                :key="index"
                :item="childItem"
                :current-item="currentItem"
                :current-ancestors="currentAncestors"
                :current-confidence="currentConfidence"
                dusk="subItem"
            />
        </ul>
    </li>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';

export default {
    name: 'ContentList',
    props: {
        item: {
            type: Object,
            required: true,
        },
        // Passed down rather than mapped per row: a getter read inside each row
        // re-renders the whole tree on every page turn, not only when the entry moves.
        currentItem: {
            type: Object,
            default: null,
        },
        currentAncestors: {
            type: Array,
            default: () => [],
        },
        currentConfidence: {
            type: String,
            default: 'none',
            validator: value => ['exact', 'parent', 'none'].includes(value),
        },
    },
    computed: {
        ...mapGetters(['isEpub', 'isPreviewMode']),
        hasChildren() {
            return Boolean(this.item.items?.length);
        },
        hasNavigationTarget() {
            return this.isEpub ? Boolean(this.item.chapterHref) : this.item.page != null;
        },
        // `currentItem` is an entry of the same publication.table_of_contents tree
        // these rows render, so identity is enough to find it.
        isCurrentItem() {
            return this.item === this.currentItem;
        },
        isCurrentAncestor() {
            return this.currentAncestors.includes(this.item);
        },
        // A parent is a known file or spine interval. Only `none` lacks a focus target.
        isAutofocusItem() {
            return this.isCurrentItem && this.currentConfidence !== 'none';
        },
    },
    methods: {
        ...mapActions(['goToPage', 'goToLocation', 'toggleTableOfContents']),
        goToItem(item) {
            if (this.isPreviewMode) {
                return false;
            }

            this.isEpub ? this.goToLocation({ chapterHref: item.chapterHref }) : this.goToPage({ page: item.page });

            this.toggleTableOfContents();
        },
    },
};
</script>
